// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import type { Box, PageInfo } from './types';
import { clamp, nextBoxId, padBox, findRanges } from './redact';

// Bundle pdf.js's worker as a same-origin module (CSP-friendly, no CDN).
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export const MAX_FILE_BYTES = 200 * 1024 * 1024; // 200 MB guard
export const MAX_PAGES = 300;

export interface LoadedPdf {
  doc: PDFDocumentProxy;
  bytes: ArrayBuffer; // pristine copy for the assembly worker
  pages: PageInfo[];
}

/** Open a PDF: keep a pristine byte copy for pdf-lib, and a pdf.js proxy for
 *  rendering + text extraction (from a *separate* copy — pdf.js detaches the
 *  buffer it is handed). */
export async function loadPdf(bytes: ArrayBuffer): Promise<LoadedPdf> {
  const doc = await pdfjs.getDocument({
    data: new Uint8Array(bytes.slice(0)),
    isEvalSupported: false,
    // Keep everything local: never fetch external cmaps/standard fonts.
  }).promise;

  const count = Math.min(doc.numPages, MAX_PAGES);
  const pages: PageInfo[] = [];
  for (let i = 0; i < count; i++) {
    const page = await doc.getPage(i + 1);
    const vp = page.getViewport({ scale: 1 });
    pages.push({ index: i, width: vp.width, height: vp.height });
    page.cleanup();
  }
  return { doc, bytes, pages };
}

/** Render a page into `canvas` at `scale` (canvas px per PDF point). */
export async function renderPageToCanvas(
  doc: PDFDocumentProxy,
  index: number,
  scale: number,
  canvas: HTMLCanvasElement,
): Promise<void> {
  const page: PDFPageProxy = await doc.getPage(index + 1);
  const viewport = page.getViewport({ scale });
  canvas.width = Math.max(1, Math.floor(viewport.width));
  canvas.height = Math.max(1, Math.floor(viewport.height));
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('Canvas 2D is unavailable in this browser.');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  page.cleanup();
}

/**
 * Search a page's text layer for `term` and return normalized redaction boxes
 * over every match. Best-effort: matches are reviewable before export, and
 * boxes err on the side of over-covering. Returns [] if the page has no text.
 */
export async function findBoxesOnPage(
  doc: PDFDocumentProxy,
  index: number,
  term: string,
  limit = 400,
): Promise<Box[]> {
  const trimmed = term.trim();
  if (!trimmed) return [];
  const page = await doc.getPage(index + 1);
  const viewport = page.getViewport({ scale: 1 });
  const vw = viewport.width;
  const vh = viewport.height;
  const content = await page.getTextContent();
  const out: Box[] = [];

  for (const raw of content.items) {
    if (out.length >= limit) break;
    if (!('str' in raw)) continue;
    const item = raw as { str: string; transform: number[]; width: number };
    const str = item.str;
    if (!str || item.width <= 0) continue;
    const ranges = findRanges(str, trimmed);
    if (ranges.length === 0) continue;

    const tx = pdfjs.Util.transform(viewport.transform, item.transform);
    const fontHeight = Math.hypot(tx[2], tx[3]) || Math.abs(tx[3]) || 1;
    const runWidth = item.width; // device px at scale 1
    const axisAligned = Math.abs(tx[1]) < 0.5 && Math.abs(tx[2]) < fontHeight; // horizontal-ish
    const originX = tx[4];
    const baselineY = tx[5];
    const len = str.length || 1;

    for (const [a, b] of ranges) {
      if (out.length >= limit) break;
      let left: number;
      let width: number;
      if (axisAligned) {
        left = originX + (a / len) * runWidth;
        width = ((b - a) / len) * runWidth;
      } else {
        left = originX;
        width = runWidth;
      }
      const top = baselineY - fontHeight;
      const box: Box = {
        id: nextBoxId(),
        x: clamp(left / vw, 0, 1),
        y: clamp(top / vh, 0, 1),
        w: clamp(width / vw, 0, 1),
        h: clamp((fontHeight * 1.08) / vh, 0, 1),
      };
      out.push(padBox(box, 0.004));
    }
  }
  page.cleanup();
  return out;
}
